import type { BellAlertRouteConfig } from "../../../contexts/app/bellAlertsContext";
import { Permission } from "../../../contexts/app/permissions";
import type { PermissionKey } from "../../../contexts/app/permissions";
import type { MenuTileCardId, MenuTileCore, MenuTileUi } from "../../../api/zekay/menu";
import {
  BILLING_CATALOGUE_SIDEBAR_KEYS,
  PLANNING_BOOKING_SIDEBAR_KEYS,
  RENTAL_CATALOGUE_SIDEBAR_KEYS,
  RENTAL_SIDEBAR_KEYS,
  USERS_SIDEBAR_LIST_KEYS,
} from "../../../api/zekay/permissions";

export type SidebarItemRightsConfig = {
  id: string;
  label: string;
  anyOf?: PermissionKey[];
  allOf?: PermissionKey[];
  hideWhenDesync?: boolean;
};

function TopSegmentFromPath(path: string): string {
  return (path || "/").split("/")[1] || "";
}

export function BuildAlertRouteConfigFromMenuTiles(tiles: MenuTileCore[]): BellAlertRouteConfig {
  const cfg: BellAlertRouteConfig = {};
  const rootCategories = new Set<string>();
  let rootEnabled = false;
  tiles.forEach((t) => {
    const top = TopSegmentFromPath(t.path);
    if (!t.alertsEnabled || !t.alertCategories || t.alertCategories.length === 0) return;

    rootEnabled = rootEnabled || t.alertsEnabled;
    (t.alertCategories || []).forEach((c) => rootCategories.add(c));
    if (!top) return;

    const prev = cfg[top] || { enabled: false, categories: [] as string[] };
    const nextCategories = Array.from(
      new Set([...(prev.categories || []), ...(t.alertCategories || [])]),
    );
    cfg[top] = {
      enabled: prev.enabled || false || t.alertsEnabled,
      categories: nextCategories,
    };
  });
  cfg[""] = { enabled: rootEnabled, categories: Array.from(rootCategories) };
  return cfg;
}

export const MENU_TILE_CARD_ORDER: readonly MenuTileCardId[] = [
  "organisation",
  "centre",
  "ventes",
] as const;

const MENU_TILE_CARD_TITLES: Record<MenuTileCardId, string> = {
  organisation: "Organisation",
  centre: "Centre",
  ventes: "Ventes",
};

export function getMenuCardTitle(id: MenuTileCardId): string {
  return MENU_TILE_CARD_TITLES[id];
}

export function GetMenuTilesCoreDefinition(): MenuTileCore[] {
  return [
    {
      path: "/planning",
      label: "Planning",
      showOnMobile: true,
      menuCard: "organisation",
      access: Permission.PLANNING_ACCESS,
      enabled: true,
    },
    {
      path: "/hr",
      label: "RH",
      showOnMobile: true,
      menuCard: "organisation",
      access: Permission.LEAVE_ACCESS,
      alertsEnabled: true,
      alertCategories: ["RH"],
      enabled: true,
    },
    {
      path: "/users",
      label: "Utilisateurs",
      showOnMobile: true,
      menuCard: "organisation",
      access: Permission.USERS_ACCESS,
      alertsEnabled: true,
      alertCategories: ["Utilisateurs"],
      enabled: true,
    },
    {
      path: "/communication",
      label: "Communication",
      showOnMobile: false,
      menuCard: "organisation",
      access: Permission.COMMUNICATION_ACCESS,
      enabled: true,
    },
    {
      path: "/excursions",
      label: "Sorties",
      showOnMobile: true,
      menuCard: "centre",
      access: Permission.EXCURSIONS_ACCESS,
      enabled: true,
    },
    {
      path: "/customers",
      label: "Clients",
      showOnMobile: true,
      menuCard: "centre",
      access: Permission.CUSTOMERS_ACCESS,
      enabled: true,
    },
    {
      path: "/trainings",
      label: "Formations",
      showOnMobile: true,
      menuCard: "centre",
      access: Permission.TRAININGS_ACCESS,
      alertsEnabled: true,
      alertCategories: ["Formations"],
      enabled: true,
    },
    {
      path: "/inventory",
      label: "Équipements",
      showOnMobile: true,
      menuCard: "centre",
      access: Permission.EQUIPMENT_ACCESS,
      alertsEnabled: true,
      alertCategories: ["Équipement"],
      enabled: true,
    },
    {
      path: "/centre",
      label: "Identification du Centre",
      showOnMobile: true,
      menuCard: "centre",
      access: Permission.CENTRE_ACCESS,
      enabled: true,
    },
    {
      path: "/billing",
      label: "Produits",
      showOnMobile: false,
      menuCard: "ventes",
      access: Permission.BILLING_ACCESS,
      enabled: true,
      showOnlyWhenDesync: true,
    },
    {
      path: "/rental",
      label: "Location",
      showOnMobile: true,
      menuCard: "ventes",
      access: Permission.RENTAL_ACCESS,
      enabled: true,
    },
    {
      path: "/events",
      label: "Événements",
      showOnMobile: true,
      menuCard: "ventes",
      access: Permission.EVENTS_ACCESS,
      enabled: true,
    },
    {
      path: "/stats",
      label: "Statistiques",
      showOnMobile: true,
      menuCard: "ventes",
      access: Permission.STATS_ACCESS,
      enabled: true,
    },
  ];
}

export type MenuGroup = { id: MenuTileCardId; tiles: MenuTileUi[] };

export function buildMenuGroups(tiles: MenuTileUi[], isMobile: boolean): MenuGroup[] {
  const visible = tiles.filter((t) => t.showWhen && (!isMobile || t.showOnMobile));
  const out: MenuGroup[] = [];

  for (const id of MENU_TILE_CARD_ORDER) {
    const group = visible.filter((t) => t.menuCard === id);
    if (group.length > 0) out.push({ id, tiles: group });
  }

  return out;
}

export function GetModulePathAccessPermission(): Record<string, PermissionKey> {
  const out: Record<string, PermissionKey> = {};
  for (const t of GetMenuTilesCoreDefinition()) {
    const seg = TopSegmentFromPath(t.path);
    if (!seg) continue;
    out[seg] = t.access;
  }
  return out;
}

export function GetAlertCategoriesForPathname(pathname: string): string[] {
  const path = pathname || "/";
  const top = path.split("/")[1] || "";
  const tiles = GetMenuTilesCoreDefinition();

  // Menu root: union of all enabled categories so we can show badges on tiles.
  if (top === "") {
    const set = new Set<string>();
    tiles.forEach((t) => {
      if (!t.alertsEnabled) return;
      (t.alertCategories || []).forEach((c) => set.add(c));
    });
    return Array.from(set);
  }

  const tile = tiles.find((t) => t.path === `/${top}`);
  if (!tile?.alertsEnabled) return [];
  return tile.alertCategories || [];
}

export function GetSidebarItemsRightsConfig(): Record<string, SidebarItemRightsConfig[]> {
  return {
    planning: [
      {
        id: "planning.calendar",
        label: "Planning",
        allOf: [Permission.PLANNING_ACCESS],
        anyOf: [
          Permission.PLANNING_ME_VIEW,
          Permission.PLANNING_COMPANY_VIEW,
          Permission.PLANNING_ALL_VIEW,
        ],
      },
      {
        id: "planning.bookings",
        label: "Prestations",
        allOf: [Permission.PLANNING_ACCESS],
        anyOf: PLANNING_BOOKING_SIDEBAR_KEYS,
      },
    ],
    hr: [
      { id: "hr.leave", label: "Mes congés", allOf: [Permission.LEAVE_CREATE] },
      {
        id: "hr.validation",
        label: "Validation",
        allOf: [Permission.LEAVE_VALIDATE],
      },
      {
        id: "hr.balances",
        label: "Compteurs",
        allOf: [Permission.LEAVE_MANAGE_BALANCES],
      },
      {
        id: "hr.types",
        label: "Types",
        anyOf: [Permission.LEAVE_TYPES_VIEW, Permission.LEAVE_TYPES_EDIT],
      },
    ],
    trainings: [
      {
        id: "trainings.cards",
        label: "Cartes",
        allOf: [Permission.TRAININGS_VIEW],
      },
      {
        id: "trainings.list",
        label: "Formations",
        allOf: [Permission.TRAININGS_VIEW],
      },
      {
        id: "trainings.templates",
        label: "Modèles",
        allOf: [Permission.TRAININGS_VIEW],
      },
    ],
    customers: [
      {
        id: "customers.list",
        label: "Clients",
        allOf: [Permission.CUSTOMERS_VIEW],
      },
      {
        id: "customers.qualifications",
        label: "Qualifications",
        allOf: [Permission.CUSTOMERS_QUALIFICATIONS_VIEW],
        hideWhenDesync: true,
      },
    ],
    inventory: [
      {
        id: "inventory.equipments",
        label: "Équipements",
        allOf: [Permission.EQUIPMENT_VIEW],
      },
      {
        id: "inventory.models",
        label: "Modèles",
        allOf: [Permission.EQUIPMENT_MODELS_VIEW],
      },
    ],
    stats: [
      {
        id: "stats.overview",
        label: "Statistiques",
        anyOf: [Permission.STATS_GENERAL_VIEW, Permission.STATS_INSTRUCTORS_VIEW],
      },
    ],
    billing: [
      {
        id: "billing.prestations",
        label: "Prestations",
        anyOf: BILLING_CATALOGUE_SIDEBAR_KEYS,
      },
      {
        id: "billing.products",
        label: "Produits",
        anyOf: BILLING_CATALOGUE_SIDEBAR_KEYS,
      },
      {
        id: "billing.rental",
        label: "Location",
        anyOf: RENTAL_CATALOGUE_SIDEBAR_KEYS,
      },
    ],
    excursions: [
      {
        id: "excursions.planning",
        label: "Sorties",
        allOf: [Permission.EXCURSIONS_VIEW],
      },
      {
        id: "excursions.params",
        label: "Paramètres",
        allOf: [Permission.EXCURSIONS_EDIT],
      },
    ],
    events: [
      {
        id: "events.list",
        label: "Événements",
        anyOf: [Permission.EVENTS_VIEW, Permission.EVENTS_EDIT],
      },
    ],
    rental: [
      {
        id: "rental.list",
        label: "Locations",
        anyOf: RENTAL_SIDEBAR_KEYS,
      },
    ],
    users: [
      {
        id: "users.list",
        label: "Utilisateurs",
        anyOf: USERS_SIDEBAR_LIST_KEYS,
      },
      {
        id: "users.roles",
        label: "Rôles",
        anyOf: [Permission.USERS_ROLES_VIEW, Permission.USERS_ROLES_EDIT],
      },
      {
        id: "users.settings",
        label: "Paramètres",
        anyOf: [Permission.USERS_ALL_EDIT, Permission.USERS_COMPANY_EDIT],
      },
    ],
    communication: [
      {
        id: "communication.mails",
        label: "Emails",
        allOf: [Permission.COMMUNICATION_MAIL_TEMPLATES_VIEW],
      },
      {
        id: "communication.sms",
        label: "SMS",
        allOf: [Permission.COMMUNICATION_SMS_TEMPLATES_VIEW],
      },
      {
        id: "communication.googleGroups",
        label: "Google Groups",
        anyOf: [
          Permission.COMMUNICATION_GOOGLE_GROUPS_VIEW,
          Permission.COMMUNICATION_GOOGLE_GROUPS_EDIT,
        ],
      },
      {
        id: "communication.settings",
        label: "Paramètres",
        anyOf: [
          Permission.COMMUNICATION_SETTINGS_VIEW,
          Permission.COMMUNICATION_SETTINGS_EDIT,
          Permission.COMMUNICATION_SMS_TEMPLATES_VIEW,
        ],
      },
    ],
    centre: [
      {
        id: "centre.info",
        label: "Infos",
        anyOf: [Permission.CENTRE_INFO_VIEW, Permission.CENTRE_INFO_EDIT],
      },
      {
        id: "centre.mobileDevices",
        label: "Appareils mobiles",
        anyOf: [
          Permission.CENTRE_DEVICES_VIEW,
          Permission.CENTRE_DEVICES_PAIR,
          Permission.CENTRE_DEVICES_REVOKE,
        ],
      },
    ],
  };
}
